import React, { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { BpInput } from '../../../../shared/components/forms/input/BpInput'
import { IEditAffiliateFormData } from '../../../ad-overview/widgets/edit-affiliate-modal/EditAffiliateForm'
import { BpButton } from '../../../../shared/components/buttons/bp-button/BpButton'
import { useAppDispatch } from '../../../../app/store/hooks/Redux'
import { hideModal } from '../../../../app/store/reducers/AppSlice'
import {
  useGetAffiliateGetQuery,
  usePostAffiliateUpdateMutation,
} from '../../../../app/api'
import { Preloader } from '../../../../shared/components/cards/preloader/Preloader'
import { BpAnchor } from '../../../../shared/components/text/anchor/BpAnchor'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import styles from './EditAffiliateModal.module.scss'

export interface IPpcComplianceEditAffiliateProps {
  id: number
  projectId: string
  callback?: () => void
}

export const EditAffiliateModal = ({
  id,
  projectId,
  callback,
}: IPpcComplianceEditAffiliateProps) => {
  const { t } = useTranslation()
  const {
    control,
    handleSubmit,
    getValues,
    reset,
    formState: { isDirty },
  } = useForm<IEditAffiliateFormData>({
    mode: 'onChange',
    defaultValues: {},
  })
  const dispatch = useAppDispatch()

  const { data, isLoading, isFetching, isError } = useGetAffiliateGetQuery(
    {
      id,
      projectId: +projectId,
    },
    { selectFromResult: (state) => ({ ...state, data: state.data?.data?.items?.[0] }) },
  )
  const [update] = usePostAffiliateUpdateMutation()

  useEffect(() => {
    reset({
      name: data?.name === null ? '' : data?.name,
      comment: data?.comment,
      email: data?.email,
    })
  }, [data])

  useEffect(() => {
    isError && dispatch(hideModal())
  }, [isError])

  if (isLoading || isFetching) {
    return (
      <div className={clsx(styles.preloaderWrapper)}>
        <Preloader />
      </div>
    )
  }

  const saveChanges = (): void => {
    const values = getValues()

    update({ body: { scenario: 'form', id, ...values } }).then(() => {
      dispatch(hideModal())
      callback && callback()
    })
  }

  const AnchorList = ({ array }: { array?: string[] }) => {
    return (
      <>
        {array?.slice(0, 5).map((value, index) => (
          <BpAnchor key={index} href={`https://${value}`} target='_blank'>
            {value}
          </BpAnchor>
        ))}

        {array && array.length > 5 && (
          <span className={clsx(styles.moreLink)}>and {array.length - 5} more</span>
        )}
      </>
    )
  }

  return (
    <form className={clsx(styles.form)} onSubmit={handleSubmit(saveChanges)}>
      <div className={clsx(styles.header)}>
        <h3 className={clsx(styles.title)}>
          {t('pages.PPCCompliance.affiliate')} {data?.name ?? data?.pub_value}
        </h3>
      </div>
      <div className={clsx(styles.content)}>
        <div className={clsx(styles.contentSection)}>
          <div className={clsx(styles.row)}>
            <span className={clsx(styles.label)}>{t('pages.PPCCompliance.createdAt')}</span>{' '}
            {data?.created_at}
          </div>
          <div className={clsx(styles.row)}>
            <span className={clsx(styles.label)}>{t('pages.PPCCompliance.updatedAt')}</span>{' '}
            {data?.updated_at}
          </div>
          <div className={clsx(styles.row)}>
            <span className={clsx(styles.label)}>{t('pages.PPCCompliance.project')}</span>{' '}
            {data?.project_name}
          </div>
          <div className={clsx(styles.row)}>
            <span className={clsx(styles.label)}>{t('pages.PPCCompliance.advertiser')}</span>
            <div className={clsx(styles.advertiserCol)}>
              <AnchorList array={data?.advertisers} />
            </div>
          </div>
          <div className={clsx(styles.row)}>
            <span className={clsx(styles.label)}>{t('pages.PPCCompliance.trackingDomain')}</span>
            <div className={clsx(styles.advertiserCol)}>
              <AnchorList array={data?.domains} />
            </div>
          </div>
        </div>
        <div className={clsx(styles.formSection)}>
          <Controller
            render={({ field }) => (
              <BpInput
                {...field}
                label={t('fields.friendlyName')}
                placeholder={t('fields.enterYourName')}
              />
            )}
            control={control}
            defaultValue={data?.name === null ? '' : data?.name}
            name='name'
          />
          <Controller
            render={({ field }) => (
              <BpInput
                {...field}
                label={t('fields.affiliateEmail')}
                placeholder={t('fields.enterAffiliatesEmail')}
              />
            )}
            control={control}
            defaultValue={data?.email}
            name='email'
          />
          <Controller
            render={({ field }) => (
              <BpInput
                {...field}
                label={t('fields.comment')}
                placeholder={t('fields.enterYourComment')}
              />
            )}
            control={control}
            defaultValue={data?.comment}
            name='comment'
          />
        </div>
      </div>
      <div className={clsx(styles.footer)}>
        <BpButton className={clsx(styles.button)} disabled={!isDirty} type='submit'>
          {t('buttons.saveChanges')}
        </BpButton>
        <BpButton theme='outline' type='button' onClick={() => dispatch(hideModal())}>
          {t('buttons.cancel')}
        </BpButton>
      </div>
    </form>
  )
}
